import type { TFunction } from 'i18next';

/**
 * Turning a role as the API returns it into a role a person can read.
 *
 * Roles arrive from `/me` and the staff directory as their stored names, and
 * those names come from two different worlds. System roles are seeded under
 * machine codes — `TENANT_ADMIN`, `SUPER_ADMIN` — because the backend matches on
 * them; custom roles carry whatever the tenant admin typed, "MOYA Approver". A
 * screen that prints the stored name serves the first group SHOUTING_SNAKE_CASE,
 * which reads as a leaked database value rather than an answer to "what am I
 * here".
 *
 * So codes are translated and everything else is left exactly as authored — a
 * custom role's name is the name its author chose, and second-guessing that
 * would be the same mistake in the other direction.
 */

/** The system roles seeded by `seed-core.ts`; each needs a label in every locale. */
export const SYSTEM_ROLES = ['SUPER_ADMIN', 'TENANT_ADMIN', 'EVALUATOR'] as const;

/** A stored name that is a machine code rather than something a person wrote. */
const CODE = /^[A-Z0-9]+(?:_[A-Z0-9]+)*$/;

/** `TENANT_ADMIN` → `Tenant Admin`. The fallback, for a code with no label yet. */
function humanise(code: string): string {
  return code
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * The display name for one role.
 *
 * A code with no translation falls back to its humanised form rather than the
 * raw key path i18next would otherwise render: a future system role should read
 * as "Data Steward" while someone writes the real label, never as
 * `roles.DATA_STEWARD`.
 */
export function roleLabel(role: string, t: TFunction): string {
  if (!CODE.test(role)) return role;
  return t(`roles.${role}`, { defaultValue: humanise(role) });
}
